import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  CircularProgress,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { getBlogByCountry, getBlogById } from "../../services/BlogService";
import { CustomButton } from "../../utils/CustomTagComponents";

const BlogDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        setLoading(true);
        // Fetch single blog
        const { data } = await getBlogById(id);
        if (data?.success) {
          const blogData = data.data;
          setBlog(blogData);
          setRelatedBlogs(data.related_blogs || []);
        }
      } catch (err) {
        console.error("Error fetching blog:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogData();
  }, [id]);

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="70vh"
      >
        <CircularProgress />
      </Box>
    );

  if (!blog)
    return (
      <Container sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h5" color="text.secondary">
          Blog not found.
        </Typography>
      </Container>
    );

  return (
    <Container maxWidth="lg" sx={{
      py: { xs: 3, sm: 4, md: 6 },
      px: { xs: 3, sm: 4, md: 6 },
      paddingLeft: { xs: "24px !important", md: "48px !important" },
      paddingRight: { xs: "24px !important", md: "48px !important" }
    }}>
      {/* 📰 Blog Header */}
      <Typography
        variant="h3"
        fontWeight={700}
        gutterBottom
        dangerouslySetInnerHTML={{ __html: blog.title }}
      />
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {new Date(blog.blog_published_on).toLocaleDateString()}
      </Typography>

      {/* 🖼️ Blog Image */}
      {blog.blog_image_url && (
        <Box my={3}>
          <img
            src={blog.blog_image_url}
            alt="blog visual"
            style={{
              width: "100%",
              maxHeight: "500px",
              objectFit: "cover",
              borderRadius: "16px",
            }}
          />
        </Box>
      )}

      {/* 📝 Blog Description */}
      <Typography
        variant="body1"
        sx={{ lineHeight: 1.8, mb: 6, textAlign: "justify" }}
        dangerouslySetInnerHTML={{ __html: blog.desc }}
      />

      {/* 📖 Related Blogs */}
      {relatedBlogs.length > 0 && (
        <>
          <Typography variant="h5" fontWeight={600} mb={3}>
            Related Blogs
          </Typography>
          <Grid container spacing={3}>
            {relatedBlogs.map((blog, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Card
                  sx={{
                    borderRadius: "20px",
                    overflow: "hidden",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    border: "1px solid #9D9D9D",
                    cursor: "pointer",
                    transition: "transform 0.2s",
                    "&:hover": { transform: "scale(1.02)" },
                  }}
                  onClick={() => navigate(`/blog/${blog.id}`)}
                >
                  <CardMedia
                    component="img"
                    image={blog.blog_image_url}
                    alt={blog.title}
                    sx={{ maxHeight: 230, objectFit: "cover" }}
                  />
                  <CardContent sx={{ flexGrow: 1, pl: 3, pr: 5 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: { xs: 18, sm: 22 },
                        fontWeight: 700,
                        color: "#000000",
                        lineHeight: { xs: "24px", sm: "34px" },
                        mb: 2,
                        // letterSpacing: "0.5px",
                      }}
                      dangerouslySetInnerHTML={{ __html: blog.title }}
                    />
                    <CustomButton variant="contained">Read More</CustomButton>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Container>
  );
};

export default BlogDetailPage;
